import { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { X, ShieldCheck, UploadSimple, PaperPlaneTilt, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export function coiStatus(coi) {
  if (!coi?.expires_on) return "none";
  return new Date(coi.expires_on + "T23:59:59") < new Date() ? "expired" : "current";
}

const PILL = {
  none: { label: "No COI", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  current: { label: "COI Current", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  expired: { label: "COI Expired", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

export function CoiPill({ coi, testId }) {
  const s = coiStatus(coi);
  const p = PILL[s];
  return (
    <span data-testid={testId} data-status={s} title={coi?.expires_on ? `Expires ${coi.expires_on}` : "No certificate on file"}
          className={`inline-block text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap ${p.cls}`}>
      {p.label}{coi?.expires_on ? <span className="ml-1 font-mono normal-case tracking-normal">{coi.expires_on}</span> : null}
    </span>
  );
}

// One current Certificate of Insurance per client: upload/replace, view, email, remove.
export default function ClientCoiModal({ client, canEdit, canDelete, onClose, onChanged }) {
  const [coi, setCoi] = useState(client.coi || null);
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const status = coiStatus(coi);

  const pickFile = () => {
    if (!expires) { toast.error("Enter the certificate's expiration date first."); return; }
    fileRef.current?.click();
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max file size is 10 MB."); return; }
    const fd = new FormData(); fd.append("file", f); fd.append("expires_on", expires);
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/coi`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoi(data.coi); setExpires("");
      toast.success(`COI ${data.replaced ? "replaced" : "uploaded"} for ${client.name} — valid through ${data.coi.expires_on}.`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the COI on file for ${client.name}?`)) return;
    setBusy(true);
    try { await api.delete(`/clients/${client.id}/coi`); setCoi(null); toast.success("COI removed."); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    const list = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Enter at least one email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/coi/send`, { to: list, note: note || null });
      toast.success(`COI sent to ${data.sent_to.join(", ")} (copy BCC'd to billing@).`);
      setCoi((c) => ({ ...c, last_sent_at: data.last_sent_at, last_sent_to: data.sent_to }));
      setSendOpen(false); setTo(""); setNote(""); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };

  const btn = "h-10 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-50";
  const kb = coi ? Math.max(1, Math.round((coi.size || 0) / 1024)) : 0;
  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="coi-modal" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md p-6 border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold flex items-center gap-2"><ShieldCheck size={22} weight="bold" className="text-emerald-600" /> Certificate of Insurance</h2>
          <button type="button" onClick={onClose} data-testid="coi-modal-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="text-sm text-slate-500 mb-4">{client.name}</div>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="coi-upload-input" onChange={upload} />

        <div className={`rounded-md border p-4 mb-4 ${status === "current" ? "bg-emerald-50 border-emerald-200" : status === "expired" ? "bg-rose-50 border-rose-300" : "bg-slate-50 border-slate-200"}`} data-testid="coi-status-box">
          <CoiPill coi={coi} testId="coi-status-pill" />
          {coi ? (
            <div className="mt-2 text-sm text-slate-700">
              <div className="font-semibold truncate" title={coi.filename}>{coi.filename} <span className="text-slate-400 font-normal">· {kb} KB</span></div>
              <div className="text-xs text-slate-500 mt-0.5">Uploaded {String(coi.uploaded_at || "").slice(0, 10)} by {coi.uploaded_by}</div>
              {coi.last_sent_at && <div className="text-xs text-slate-500">Last sent {String(coi.last_sent_at).slice(0, 10)} to {(coi.last_sent_to || []).join(", ")}</div>}
              {status === "expired" && <div className="text-xs font-semibold text-rose-700 mt-1">Upload the renewed certificate — it will replace this one.</div>}
            </div>
          ) : <div className="mt-2 text-sm text-slate-600">No certificate on file for this client.</div>}
        </div>

        {coi && (
          <div className="flex flex-wrap gap-2 mb-5">
            <a href={`${API_BASE}/clients/${client.id}/coi/file`} target="_blank" rel="noreferrer" data-testid="coi-view-btn"
               className={`${btn} border-slate-300 hover:bg-slate-50`}><ArrowSquareOut size={16} weight="bold" /> View</a>
            {canEdit && (
              <button type="button" onClick={() => setSendOpen((v) => !v)} disabled={busy || status === "expired"} data-testid="coi-send-toggle"
                      title={status === "expired" ? "Expired — upload the current COI first" : "Email the COI"}
                      className={`${btn} border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700`}><PaperPlaneTilt size={16} weight="bold" /> Send COI</button>
            )}
            {canDelete && <button type="button" onClick={remove} disabled={busy} data-testid="coi-delete-btn" className={`${btn} border-slate-300 text-rose-600 hover:bg-rose-50`}><Trash size={16} weight="bold" /> Remove</button>}
          </div>
        )}

        {sendOpen && coi && (
          <form onSubmit={send} className="mb-5 border border-emerald-200 rounded-md p-3 bg-emerald-50/50" data-testid="coi-send-form">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Send to</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} required placeholder="name@client.com, second@client.com" data-testid="coi-send-to"
                   className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-3 block">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="coi-send-note" placeholder="Hi Pat — here's the COI you requested for the Dayton project."
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            <div className="mt-3 flex gap-2 justify-end">
              <button type="button" onClick={() => setSendOpen(false)} className={`${btn} border-slate-300`}>Cancel</button>
              <button disabled={busy} data-testid="coi-send-submit" className={`${btn} border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700`}>{busy ? "Sending…" : "Send"}</button>
            </div>
          </form>
        )}

        {canEdit && (
          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{coi ? "Replace with a renewed certificate" : "Upload certificate"}</div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs text-slate-500">Expiration date</label>
                <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="coi-expires-input"
                       className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
              </div>
              <button type="button" onClick={pickFile} disabled={busy} data-testid="coi-upload-btn"
                      className={`${btn} border-slate-900 bg-slate-900 text-white hover:bg-slate-800`}><UploadSimple size={16} weight="bold" /> {busy ? "Uploading…" : "Choose file"}</button>
            </div>
            <div className="text-xs text-slate-400 mt-2">PDF, JPG or PNG · max 10 MB. {coi ? "The previous certificate is removed — only the current one is kept." : ""}</div>
          </div>
        )}
      </div>
    </div>
  );
}
